import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Stack,
  PinInput,
  Alert,
  Group,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconMail,
  IconLock,
  IconArrowRight,
  IconCheck,
  IconArrowLeft,
} from "@tabler/icons-react";
import { Endpoints } from "../../lib/endpoints";
import { PerformRequest } from "../../hooks/usePerformRequest";
import "./styles.css";

type ResetFormValues = {
  email: string;
  newPassword: string;
  confirmPassword: string;
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<"email" | "code" | "password">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  const form = useForm<ResetFormValues>({
    initialValues: {
      email: "",
      newPassword: "",
      confirmPassword: "",
    },
    validate: {
      email: (value: string) =>
        /^\S+@\S+$/.test(value) ? null : "Invalid email",
      newPassword: (value) =>
        value.length < 6 ? "Password must be at least 6 characters" : null,
      confirmPassword: (value, values) =>
        value !== values.newPassword ? "Passwords do not match" : null,
    },
  });

  useEffect(() => {
    document.title = "Reset Password | Virel Admin";
  }, []);

  // Cooldown timer
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => {
        setCooldownSeconds(cooldownSeconds - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);

  const handleRequestCode = async (values: ResetFormValues) => {
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      const response = await PerformRequest({
        ...Endpoints.RequestPasswordReset(),
        data: { email: values.email },
      });

      if (response.status === 200) {
        setEmail(values.email);
        setStep("code");
        setSuccess("Reset code sent to your email");
        setCooldownSeconds(60); // 1 minute cooldown
      } else {
        setError(response.message || "Failed to send reset code");
      }
    } catch (err: any) {
      setError(err.message || "Failed to send reset code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (cooldownSeconds > 0) return;

    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      const response = await PerformRequest({
        ...Endpoints.RequestPasswordReset(),
        data: { email },
      });

      if (response.status === 200) {
        setSuccess("Reset code resent to your email");
        setCooldownSeconds(60);
      } else {
        setError(response.message || "Failed to resend code");
      }
    } catch (err: any) {
      setError(err.message || "Failed to resend code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (code.length !== 6) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    setError(null);
    setStep("password");
  };

  const handleResetPassword = async (values: ResetFormValues) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await PerformRequest({
        ...Endpoints.ResetPassword(),
        data: {
          email,
          code,
          newPassword: values.newPassword,
        },
      });

      if (response.status === 200) {
        setSuccess("Password reset successfully! Redirecting to login...");
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        setError(response.message || "Failed to reset password");
      }
    } catch (err: any) {
      setError(err.message || "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="admin-login">
      <div className="login-background">
        <div className="login-orb orb-1"></div>
        <div className="login-orb orb-2"></div>
        <div className="login-orb orb-3"></div>
        <div className="login-grid"></div>
      </div>

      <Container size="lg" className="login-container">
        <div className="login-shell">
          <div className="login-brand">
            <div className="login-brand-top">
              <div className="login-logo">
                <img src="/icon.png" alt="Virel Logo" style={{ height: 40 }} />
              </div>
              <div>
                <Text className="login-brand-kicker">Virel</Text>
                <Title order={1} className="login-brand-title">
                  Admin Dashboard
                </Title>
              </div>
            </div>

            <Text className="login-brand-subtitle">
              Reset your password to regain access to your admin account.
            </Text>
          </div>

          <Paper className="login-card" shadow="xl">
            <Stack gap="lg" className="login-content">
              <div className="login-header">
                <Title order={2} className="login-title">
                  {step === "email"
                    ? "Reset Password"
                    : step === "code"
                      ? "Enter Reset Code"
                      : "Set New Password"}
                </Title>
                <Text size="sm" c="dimmed" className="login-subtitle">
                  {step === "email"
                    ? "Enter your email address to receive a reset code."
                    : step === "code"
                      ? `Enter the 6-digit code sent to ${email}`
                      : "Enter your new password"}
                </Text>
              </div>

              {success && (
                <Alert color="green" variant="light">
                  {success}
                </Alert>
              )}

              {error && (
                <Alert color="red" variant="light">
                  {error}
                </Alert>
              )}

              {step === "email" && (
                <form onSubmit={form.onSubmit(handleRequestCode)}>
                  <Stack gap="md">
                    <TextInput
                      placeholder="you@email.com"
                      label="Email"
                      size="md"
                      leftSection={<IconMail size={18} />}
                      classNames={{
                        input: "login-input",
                        label: "login-label",
                      }}
                      {...form.getInputProps("email")}
                    />

                    <Button
                      type="submit"
                      size="md"
                      fullWidth
                      loading={isLoading}
                      rightSection={<IconArrowRight size={18} />}
                      className="login-button"
                    >
                      Send Reset Code
                    </Button>

                    <Button
                      variant="subtle"
                      size="sm"
                      fullWidth
                      leftSection={<IconArrowLeft size={16} />}
                      onClick={() => navigate("/login")}
                    >
                      Back to Login
                    </Button>
                  </Stack>
                </form>
              )}

              {step === "code" && (
                <Stack gap="md">
                  <PinInput
                    size="xl"
                    length={6}
                    type="number"
                    value={code}
                    onChange={setCode}
                    oneTimeCode
                    autoFocus
                    className="login-pin-input"
                  />

                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Didn't receive the code?
                    </Text>
                    <Button
                      variant="subtle"
                      size="sm"
                      onClick={handleResendCode}
                      disabled={cooldownSeconds > 0 || isLoading}
                    >
                      {cooldownSeconds > 0
                        ? `Resend in ${cooldownSeconds}s`
                        : "Resend Code"}
                    </Button>
                  </Group>

                  <Button
                    onClick={handleVerifyCode}
                    size="md"
                    fullWidth
                    disabled={code.length !== 6}
                    className="login-button"
                  >
                    Verify Code
                  </Button>

                  <Button
                    variant="subtle"
                    size="sm"
                    fullWidth
                    leftSection={<IconArrowLeft size={16} />}
                    onClick={() => {
                      setStep("email");
                      setCode("");
                      setError(null);
                    }}
                  >
                    Back
                  </Button>
                </Stack>
              )}

              {step === "password" && (
                <form onSubmit={form.onSubmit(handleResetPassword)}>
                  <Stack gap="md">
                    <PasswordInput
                      placeholder="Enter new password"
                      label="New Password"
                      size="md"
                      leftSection={<IconLock size={18} />}
                      classNames={{
                        input: "login-input",
                        label: "login-label",
                      }}
                      {...form.getInputProps("newPassword")}
                    />

                    <PasswordInput
                      placeholder="Confirm new password"
                      label="Confirm Password"
                      size="md"
                      leftSection={<IconLock size={18} />}
                      classNames={{
                        input: "login-input",
                        label: "login-label",
                      }}
                      {...form.getInputProps("confirmPassword")}
                    />

                    <Button
                      type="submit"
                      size="md"
                      fullWidth
                      loading={isLoading}
                      rightSection={<IconCheck size={18} />}
                      className="login-button"
                    >
                      Reset Password
                    </Button>

                    <Button
                      variant="subtle"
                      size="sm"
                      fullWidth
                      leftSection={<IconArrowLeft size={16} />}
                      onClick={() => {
                        setStep("code");
                        form.setFieldValue("newPassword", "");
                        form.setFieldValue("confirmPassword", "");
                        setError(null);
                      }}
                    >
                      Back
                    </Button>
                  </Stack>
                </form>
              )}
            </Stack>
          </Paper>
        </div>
      </Container>
    </div>
  );
};

export default ResetPassword;
